import type {
  OverviewTeamMember,
  OverviewTopLabel,
  OverviewTopProduct,
} from "../../api/overview-types";
import {
  ANALYTICS_TABLE,
  ANALYTICS_TD,
  ANALYTICS_TH,
  ChartCard,
} from "../../components/analytics/cards";
import { labelDisplayName } from "../../lib/label-display";
import { looksTechnical } from "../../lib/technical-text";
import { useOverviewFormat } from "./useOverviewFormat";

/** A name that is safe to show: never a raw id / key (those fall back to a dash). */
const shown = (name: string) => (name && !looksTechnical(name) ? name : "—");

/** OWN-164 team table: per-member workload and speed. Scrolls sideways on a phone. */
export function TeamTable({ team }: { readonly team: readonly OverviewTeamMember[] }) {
  const f = useOverviewFormat();
  const { t } = f;
  return (
    <ChartCard title={t("dashboard.overview.section.team")}>
      <div className="overflow-x-auto" data-testid="overview-team-scroll">
        <table
          className={`${ANALYTICS_TABLE} min-w-[32rem]`}
          data-testid="overview-team"
          aria-label={t("dashboard.overview.section.team")}
        >
          <thead>
            <tr>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.team.member")}
              </th>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.team.handled")}
              </th>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.team.replies")}
              </th>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.team.firstResponse")}
              </th>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.team.openNow")}
              </th>
            </tr>
          </thead>
          <tbody>
            {team.map((m) => (
              <tr key={m.userId} data-testid="overview-team-row">
                <th scope="row" className={`${ANALYTICS_TD} font-medium`}>
                  {shown(m.name)}
                </th>
                <td className={ANALYTICS_TD}>{f.num(m.conversationsHandled)}</td>
                <td className={ANALYTICS_TD}>{f.num(m.replies)}</td>
                <td className={ANALYTICS_TD}>
                  {m.medianFirstResponseSeconds === null
                    ? "—"
                    : f.duration(m.medianFirstResponseSeconds)}
                </td>
                <td className={ANALYTICS_TD}>{f.num(m.openNow)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

/** OWN-164 top products: units sold, plus revenue when the caller may see money. */
export function TopProducts({
  products,
  currency,
}: {
  readonly products: readonly OverviewTopProduct[];
  readonly currency: string;
}) {
  const f = useOverviewFormat();
  const { t } = f;
  const withRevenue = products.some((p) => p.revenueMinor !== null);
  return (
    <ChartCard title={t("dashboard.overview.section.topProducts")}>
      <div className="overflow-x-auto">
        <table
          className={`${ANALYTICS_TABLE} min-w-[18rem]`}
          data-testid="overview-top-products"
          aria-label={t("dashboard.overview.section.topProducts")}
        >
          <thead>
            <tr>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.products.name")}
              </th>
              <th scope="col" className={ANALYTICS_TH}>
                {t("dashboard.overview.products.quantity")}
              </th>
              {withRevenue ? (
                <th scope="col" className={ANALYTICS_TH}>
                  {t("dashboard.overview.products.revenue")}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.productId}>
                <th scope="row" className={`${ANALYTICS_TD} font-medium`}>
                  {shown(p.name)}
                </th>
                <td className={ANALYTICS_TD}>{f.num(p.quantity)}</td>
                {withRevenue ? (
                  <td className={ANALYTICS_TD}>
                    {p.revenueMinor === null ? "—" : f.money(p.revenueMinor, currency)}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

/** OWN-164 labels most applied to customers this week. */
export function TopLabels({ labels }: { readonly labels: readonly OverviewTopLabel[] }) {
  const f = useOverviewFormat();
  const { t } = f;
  return (
    <ChartCard title={t("dashboard.overview.section.topLabels")}>
      <ul data-testid="overview-top-labels" className="m-0 flex list-none flex-col p-0">
        {labels.map((l) => (
          <li
            key={l.labelId}
            className="flex items-center justify-between gap-3 border-b border-line py-2 text-sm last:border-b-0"
          >
            <span className="min-w-0 font-medium break-words text-fg">
              {shown(labelDisplayName(l, t))}
            </span>
            <span className="shrink-0 text-fg-muted tabular-nums">
              {t("dashboard.overview.labels.count", { count: l.count })}
            </span>
          </li>
        ))}
      </ul>
    </ChartCard>
  );
}
